import { apiUrl } from '@algoan/dashboard/env';
import { AsyncThunk } from '@algoan/dashboard/store/store';
import {
  fetchActivityCashFlowFailure,
  fetchActivityCashFlowStart,
  fetchActivityCashFlowSuccess,
  fetchActivityEntryFailure,
  fetchActivityEntryStart,
  fetchActivityEntrySuccess,
  resetState,
  fetchTransactionsSuccess,
} from '@algoan/dashboard/store/slice';
import { activityPaginationSelector } from '@algoan/dashboard/store/selectors';
import { authRequest } from './auth-thunks';
import {
  ActivityEntry,
  ActivityError,
  PaginatedResources,
  ActivityCashFlowError,
  CashFlow,
} from '@algoan/dashboard/model';
import { buildUrl } from '@algoan/dashboard/utils';
import { fetchAccounts } from './account-thunks';
import axios, { CancelTokenSource } from 'axios';
import { batch } from 'react-redux';

const perPage = 5;
export let activityPageCancellationToken: CancelTokenSource | undefined;

export const fetchActivity = (): AsyncThunk => async (dispatch, getState) => {
  const state = getState();
  const pagination = activityPaginationSelector(state);
  activityPageCancellationToken = axios.CancelToken.source();
  if (pagination.currentPage === pagination.totalPages) {
    return;
  }

  const pageToFetch = pagination.next ? pagination.currentPage + 1 : 1;

  const activityUrl = buildUrl(`${apiUrl}/v2/history`, [
    { key: 'page', value: pageToFetch },
    { key: 'limit', value: perPage },
    { key: 'sort', value: { _id: -1 } },
  ]);

  try {
    dispatch(fetchActivityEntryStart());
    const { data: response } = await dispatch(
      authRequest<PaginatedResources<ActivityEntry>>('get', activityUrl, undefined, {
        cancelToken: activityPageCancellationToken?.token,
      }),
    );

    const { pagination: paginationData, resources: activityEntryList } = response;

    if (activityEntryList.length === 0 && pagination.currentPage === 0) {
      dispatch(fetchActivityEntryFailure({ error: ActivityError.NO_ACTIVITY_FOUND }));
      return;
    }

    dispatch(
      fetchActivityEntrySuccess({
        activityEntryList: activityEntryList,
        paginationData: { ...paginationData, currentPage: pagination.currentPage + 1 },
      }),
    );
  } catch (error) {
    dispatch(fetchActivityEntryFailure({ error: ActivityError.NO_ACTIVITY_FOUND }));
  }
};

export const fetchOneCashFlow =
  (customerId: string, analysisId: string, cashFlowId: string): AsyncThunk<CashFlow | undefined> =>
  async (dispatch) => {
    const cashFlowUrl = buildUrl(
      `${apiUrl}/v2/customers/${customerId}/analyses/${analysisId}/credit-insights/cash-flows/${cashFlowId}`,
      [
        { key: 'page', value: 0 },
        { key: 'limit', value: 1 },
        { key: 'sort', value: { _id: -1 } },
      ],
    );

    const { data: cashFlow } = await dispatch(authRequest<CashFlow>('get', cashFlowUrl));

    return cashFlow;
  };

export const resetActivity = (): AsyncThunk => async (dispatch) => {
  dispatch(resetState());
};

export const refreshActivity = (): AsyncThunk => async (dispatch) => {
  dispatch(resetActivity());
  dispatch(fetchActivity());
};

export const fetchOneActivityCashFlow =
  (customerId: string, analysisId: string, cashFlowId: string): AsyncThunk =>
  async (dispatch, getState) => {
    const { activity } = getState();

    if (activity.activityCashFlowCustomerId !== customerId || activity.activityCashFlowAnalysisId !== analysisId) {
      dispatch(
        fetchAccounts({
          customerId,
          analysisId,
          shouldFetchBalances: false,
          activityCancelToken: activityPageCancellationToken,
        }),
      );
    }

    if (activity.activityCashFlow === undefined || activity.activityCashFlow?.id !== cashFlowId) {
      try {
        dispatch(fetchActivityCashFlowStart());
        const activityCashFlow = await dispatch(fetchOneCashFlow(customerId, analysisId, cashFlowId));

        if (activityCashFlow) {
          batch(() => {
            dispatch(fetchTransactionsSuccess(activityCashFlow?.transactions));
            dispatch(
              fetchActivityCashFlowSuccess({
                analysisId,
                customerId,
                activityCashFlow,
              }),
            );
          });
        } else {
          throw ActivityCashFlowError.NO_CASHFLOW_FOUND;
        }
      } catch (error) {
        dispatch(fetchActivityCashFlowFailure({ error: error as ActivityCashFlowError }));
      }
    }
  };
